<template>
    <div class="shipment-details">
        <div class="shipment-details__header">
            <v-chip
                :color="getStatusColor(shipment.status)"
                text-color="white"
                class="shipment-details__status"
            >
                {{ getStatusText(shipment.status) }}
            </v-chip>
            <div v-if="shipment.document_link" class="shipment-details__doc-links">
                <v-btn
                    class="shipment-details__document-link-header"
                    min-width="32"
                    height="32"
                    color="transparent"
                    dark
                    @click="openLink(shipment.document_link)"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        fill="currentColor"
                        class="bi bi-file-earmark-pdf shipment-details__document-icon-header"
                        viewBox="0 0 16 16"
                    >
                        <path
                            d="M14 14V4.5L9.5 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2M9.5 3A1.5 1.5 0 0 0 11 4.5h2V14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h5.5z"
                        />
                        <path
                            d="M4.603 14.087a.8.8 0 0 1-.438-.42c-.195-.388-.13-.776.08-1.102.198-.307.526-.568.897-.787a7.7 7.7 0 0 1 1.482-.645 20 20 0 0 0 1.062-2.227 7.3 7.3 0 0 1-.43-1.295c-.086-.4-.119-.796-.046-1.136.075-.354.274-.672.65-.823.192-.077.4-.12.602-.077a.7.7 0 0 1 .477.365c.088.164.12.356.127.538.007.188-.012.396-.047.614-.084.51-.27 1.134-.52 1.794a11 11 0 0 0 .98 1.686 5.8 5.8 0 0 1 1.334.05c.364.066.734.195.96.465.12.144.193.32.2.518.007.192-.047.382-.138.563a1.04 1.04 0 0 1-.354.416.86.86 0 0 1-.51.138c-.331-.014-.654-.196-.933-.417a5.7 5.7 0 0 1-.911-.95 11.7 11.7 0 0 0-1.997.406 11.3 11.3 0 0 1-1.02 1.51c-.292.35-.609.656-.927.787a.8.8 0 0 1-.58.029m1.379-1.901q-.25.115-.459.238c-.328.194-.541.383-.647.547-.094.145-.096.25-.04.361q.016.032.026.044l.035-.012c.137-.056.355-.235.635-.572a8 8 0 0 0 .45-.606m1.64-1.33a13 13 0 0 1 1.01-.193 12 12 0 0 1-.51-.858 21 21 0 0 1-.5 1.05zm2.446.45q.226.245.435.41c.24.19.407.253.498.256a.1.1 0 0 0 .07-.015.3.3 0 0 0 .094-.125.44.44 0 0 0 .059-.2.1.1 0 0 0-.026-.063c-.052-.062-.2-.152-.518-.209a4 4 0 0 0-.612-.053zM8.078 7.8a7 7 0 0 0 .2-.828q.046-.282.038-.465a.6.6 0 0 0-.032-.198.5.5 0 0 0-.145.04c-.087.035-.158.106-.196.283-.04.192-.03.469.046.822q.036.167.09.346z"
                        />
                    </svg>
                </v-btn>
            </div>
        </div>

        <div class="shipment-details__grid">
            <div class="shipment-details__detail-item">
                <span class="shipment-details__detail-label">{{ $t('pages.shipments.shipmentDate') }}:</span>
                <span class="shipment-details__detail-value">
                    <strong><DateCell :value="shipment.created_at" :utc="false"/></strong>
                </span>
            </div>

            <div class="shipment-details__detail-item">
                <span class="shipment-details__detail-label">{{ $t('pages.shipments.usManager') }}:</span>
                <span class="shipment-details__detail-value"><strong>{{ formatSenders(shipment.senders) }}</strong></span>
            </div>

            <div class="shipment-details__detail-item">
                <span class="shipment-details__detail-label">{{ $t('pages.shipments.recipient') }}:</span>
                <span class="shipment-details__detail-value"><strong>{{ getRecipientName(shipment) }}</strong></span>
            </div>

            <div class="shipment-details__detail-item">
                <span class="shipment-details__detail-label">{{ $t('pages.shipments.employee') }}:</span>
                <span class="shipment-details__detail-value"><strong>{{ shipment.createdBy?.nickName || '-' }}</strong></span>
            </div>
        </div>

        <div class="shipment-details__summary">
            <div class="shipment-details__summary-details">
                <div class="shipment-details__summary-card">
                    <div class="shipment-details__summary-card-header">
                        <v-icon small color="#666" class="shipment-details__summary-icon">mdi-cash-multiple</v-icon>
                        <span class="shipment-details__summary-card-label">{{ $t('pages.shipments.totalPrice') }}</span>
                    </div>
                    <div class="shipment-details__summary-card-value">{{ formatPrice(shipment.sum || 0, shipment.currency)  }}</div>
                </div>
                <div class="shipment-details__summary-card shipment-details__summary-card--paid">
                    <div class="shipment-details__summary-card-header">
                        <v-icon small color="#4caf50" class="shipment-details__summary-icon">mdi-check-circle</v-icon>
                        <span class="shipment-details__summary-card-label">{{ $t('pages.shipments.paid') }}</span>
                    </div>
                    <div class="shipment-details__summary-card-value shipment-details__summary-card-value--paid">{{ formatPrice(shipment.paid_sum || 0, shipment.currency) }}</div>
                </div>
                <div class="shipment-details__summary-card shipment-details__summary-card--outstanding">
                    <div class="shipment-details__summary-card-header">
                        <v-icon small color="#ff9800" class="shipment-details__summary-icon">mdi-alert-circle</v-icon>
                        <span class="shipment-details__summary-card-label">{{ $t('pages.shipments.outstanding') }}</span>
                    </div>
                    <div class="shipment-details__summary-card-value shipment-details__summary-card-value--outstanding">{{ formatPrice(outstandingAmount, shipment.currency) }}</div>
                </div>
            </div>

            <div class="shipment-details__summary-progress">
                <div class="shipment-details__progress-wrapper">
                    <v-progress-circular
                        :value="paymentProgress"
                        :size="80"
                        :width="6"
                        :color="progressColor"
                        rotate="270"
                        class="shipment-details__progress-circle"
                    >
                        <div class="shipment-details__progress-content">
                            <span class="shipment-details__progress-percent">{{ paymentProgressRounded }}%</span>
                            <span class="shipment-details__progress-label">{{ $t('pages.shipments.paid') }}</span>
                        </div>
                    </v-progress-circular>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import languageMixin from '@/mixins/language.mixin'
import DateCell from '@/components/common/cells/date-cell.vue'
import formatSenders from '@/helpers/format-senders.utils'
import { formatPriceWithCurrency } from '@/helpers/format-price-with-currency'

export default {
    name: 'ShipmentDetails',
    components: { DateCell },
    mixins: [languageMixin],
    props: {
        shipment: {
            type: Object,
            required: true,
        },
    },
    computed: {
        outstandingAmount: function () {
            if (!this.shipment) return 0
            return (this.shipment.sum || 0) - (this.shipment.paid_sum || 0)
        },
        paymentProgress: function () {
            if (!this.shipment || !this.shipment.sum || this.shipment.sum === 0) return 0
            const paid = this.shipment.paid_sum || 0
            return Math.min(100, (paid / this.shipment.sum) * 100)
        },
        progressColor: function () {
            const progress = this.paymentProgress
            if (progress === 100) return 'success'
            if (progress >= 50) return 'primary'
            return 'warning'
        },
        paymentProgressRounded: function () {
            return Math.round(this.paymentProgress)
        },
    },
    methods: {
        formatPrice: function (price, currency = 'ILS') {
            return formatPriceWithCurrency(price, currency)
        },
        formatSenders,
        getStatusColor: function (status) {
            if (!status) return 'grey'
            const statusLower = status.toLowerCase()
            if (statusLower === 'full') return 'green'
            if (statusLower === 'partial') return 'orange'
            if (statusLower === 'unpaid') return 'red'
            return 'grey'
        },
        getStatusText: function (status) {
            if (!status) return this.$t('pages.shipments.unknown')
            return this.$t(`pages.shipments.${status.toLowerCase()}`)
        },
        getRecipientName: function (shipment) {
            if (shipment.recipient_first_name || shipment.recipient_last_name) {
                const firstName = shipment.recipient_first_name || ''
                const lastName = shipment.recipient_last_name || ''
                return `${firstName} ${lastName}`.trim() || '-'
            }
            return '-'
        },
        openLink: function (link) {
            if (link) {
                window.open(link, '_blank', 'noopener,noreferrer')
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.shipment-details {
    background: white;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    @include tablet-up {
        padding: 20px;
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        flex-wrap: wrap;
        gap: 12px;
    }

    &__grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
        margin-bottom: 20px;

        @include tablet-up {
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 16px;
        }
    }

    &__detail-item {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    &__detail-label {
        font-weight: 600;
        color: #666;
        font-size: 13px;
    }

    &__detail-value {
        color: #333;
        font-size: 14px;
    }

    &__summary {
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
        padding: 20px;
        background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        border-radius: 8px;
        align-items: center;

        @include tablet-up {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 16px;
        }
    }

    &__summary-progress {
        display: flex;
        justify-content: center;
        align-items: center;

        @include tablet-up {
            justify-content: flex-end;
        }
    }

    &__progress-wrapper {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    &__progress-circle {
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.08));
    }

    &__progress-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 4px;
    }

    &__progress-percent {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        line-height: 1.2;
        letter-spacing: -0.5px;
    }

    &__progress-label {
        font-size: 9px;
        color: #333;
        margin-top: 2px;
        text-transform: uppercase;
        letter-spacing: 0.8px;
        font-weight: 500;
    }

    &__summary-details {
        display: grid;
        grid-template-columns: 1fr;
        gap: 16px;

        @include tablet-up {
            grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        }
    }

    &__summary-card {
        background: white;
        border-radius: 8px;
        padding: 8px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        transition: transform 0.2s, box-shadow 0.2s;
        border-left: 4px solid #e0e0e0;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }

        &--paid {
            border-left-color: #4caf50;
        }

        &--outstanding {
            border-left-color: #ff9800;
        }
    }

    &__summary-card-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
    }

    &__summary-icon {
        margin-right: 0 !important;
    }

    &__summary-card-label {
        font-weight: 600;
        color: #666;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.3px;
    }

    &__summary-card-value {
        font-size: 22px;
        font-weight: 700;
        color: #333;
        line-height: 1.2;

        &--paid {
            color: #4caf50;
        }

        &--outstanding {
            color: #ff9800;
        }
    }

    &__status {
        font-size: 14px;
    }

    &__doc-links {
        display: flex;
        gap: 8px;
        align-items: center;
    }

    &__document-link-header {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-decoration: none;
        transition: opacity 0.2s;
        padding: 0;
        border-radius: 4px;
        width: 32px;
        height: 32px;
        min-width: 32px !important;

        &:hover {
            opacity: 0.7;
        }

        svg {
            display: block;
        }
    }

    &__document-icon-header {
        width: 24px;
        height: 24px;
        color: red;
    }
}
</style>

